import { useState } from "react"
import { toast } from "sonner"
import { deleteSubscriber } from "@client/admin/api"
import type { SubscriberRow } from "@client/admin/types"
import { useEntitiesStore, useRows } from "@client/store/entities"
import { BulkActionBar, BulkSelectCheckbox, useBulkSelection } from "./components/bulk-selection"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import {
  ContentEmptyState,
  ContentNav,
  ContentPagination,
  ContentStatusBadge,
} from "./content-components"
import { useHasPermission } from "./session-context"

const PAGE_SIZE = 10

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function SubscriberTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
      <div className="divide-y divide-gold-500/30">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="grid animate-pulse grid-cols-3 gap-4 px-5 py-5">
            <div className="h-4 rounded bg-cream-100" />
            <div className="h-4 w-20 rounded bg-cream-100" />
            <div className="h-4 w-24 rounded bg-cream-100" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function SubscribersPage() {
  const canMutate = useHasPermission("content_mutate")
  const subscriberState = useRows("subscriber")
  const loadedRows = subscriberState.rows as SubscriberRow[] | null
  const optimisticRemove = useEntitiesStore((state) => state.optimisticRemove)
  const optimisticRemoveMany = useEntitiesStore((state) => state.optimisticRemoveMany)
  const subscribers = loadedRows ?? []
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "subscribed" | "unsubscribed">("all")
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<SubscriberRow | null>(null)
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [bulkRemoving, setBulkRemoving] = useState(false)

  const filtered = subscribers
    .filter((row) => {
      const matchesSearch = !search.trim() || row.email.toLowerCase().includes(search.trim().toLowerCase())
      const matchesStatus = statusFilter === "all" || row.status === statusFilter
      return matchesSearch && matchesStatus
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const subscribedCount = subscribers.filter((row) => row.status === "subscribed").length
  const visibleIds = pageRows.map((row) => row.id)
  const {
    selectedIds,
    selectedCount,
    isSelected,
    isAllSelected,
    isSomeSelected,
    toggle,
    toggleAll,
    remove: removeFromSelection,
    clear,
  } = useBulkSelection(filtered.map((row) => row.id))

  const remove = async () => {
    if (!selected) return
    setRemoving(true)
    try {
      await optimisticRemove("subscriber", selected.id, () => deleteSubscriber(selected.id))
      toast.success("Subscriber removed")
      removeFromSelection(selected.id)
      setSelected(null)
    } catch {
      toast.error("Could not remove the subscriber.")
    } finally {
      setRemoving(false)
    }
  }

  const removeSelected = async () => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    setBulkRemoving(true)
    try {
      await optimisticRemoveMany("subscriber", ids, () => Promise.all(ids.map((id) => deleteSubscriber(id))))
      toast.success(`Removed ${ids.length} subscribers`)
      clear()
      setBulkRemoveOpen(false)
    } catch {
      toast.error("Could not remove the selected subscribers.")
    } finally {
      setBulkRemoving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Subscribers</h1>
        <p className="mt-1 text-sm text-ink/60">Manage the people who receive Redempta newsletter updates.</p>
      </div>

      <ContentNav />

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-[10px] border border-gold-500 bg-white p-5">
          <p className="text-sm text-ink/50">Active subscribers</p>
          <p className="mt-2 font-display text-3xl font-bold text-forest-950">
            {loadedRows === null ? "..." : subscribedCount}
          </p>
        </div>
        <div className="rounded-[10px] border border-gold-500 bg-white p-5">
          <p className="text-sm text-ink/50">Total records</p>
          <p className="mt-2 font-display text-3xl font-bold text-forest-950">
            {loadedRows === null ? "..." : subscribers.length}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="w-full lg:max-w-sm">
          <label htmlFor="subscriber-search" className="mb-1.5 block text-sm font-medium text-ink">
            Search subscribers
          </label>
          <input
            id="subscriber-search"
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search by email..."
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
        <div className="flex gap-2" role="group" aria-label="Filter subscribers by status">
          {(["all", "subscribed", "unsubscribed"] as const).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => {
                setStatusFilter(status)
                setPage(1)
              }}
              className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${
                statusFilter === status
                  ? "bg-forest-950 text-white"
                  : "border border-gold-500 bg-white text-ink/70 hover:bg-cream-100"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {canMutate && selectedCount > 0 && (
        <BulkActionBar selectedCount={selectedCount} onClear={clear}>
          <button
            type="button"
            onClick={() => setBulkRemoveOpen(true)}
            disabled={bulkRemoving || removing}
            className="rounded-full bg-red-100 px-4 py-2 text-xs font-semibold text-red-800 transition-colors hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Remove selected
          </button>
        </BulkActionBar>
      )}

      {subscriberState.error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-sm text-red-600">{subscriberState.error}</p>
          <button
            type="button"
            onClick={() => void subscriberState.retry()}
            className="mt-3 text-sm font-medium text-red-700 underline"
          >
            Try again
          </button>
        </div>
      ) : loadedRows === null ? (
        <SubscriberTableSkeleton />
      ) : pageRows.length === 0 ? (
        <ContentEmptyState message={search || statusFilter !== "all" ? "No subscribers match these filters." : "No subscribers yet."} />
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-left">
              <thead className="border-b border-gold-500/50 bg-cream-50">
                <tr>
                  {canMutate && (
                    <th className="w-12 px-5 py-4">
                      <BulkSelectCheckbox
                        checked={isAllSelected(visibleIds)}
                        indeterminate={isSomeSelected(visibleIds)}
                        onChange={() => toggleAll(visibleIds)}
                        label="Select visible subscribers"
                        disabled={bulkRemoving || visibleIds.length === 0}
                      />
                    </th>
                  )}
                  <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-ink/60">Email</th>
                  <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-ink/60">Status</th>
                  <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-ink/60">Subscribed</th>
                  {canMutate && <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-ink/60">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {pageRows.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-cream-50">
                    {canMutate && (
                      <td className="w-12 px-5 py-4">
                        <BulkSelectCheckbox
                          checked={isSelected(row.id)}
                          onChange={() => toggle(row.id)}
                          label={`Select ${row.email}`}
                          disabled={bulkRemoving}
                        />
                      </td>
                    )}
                    <td className="px-5 py-4 text-sm font-medium text-ink">{row.email}</td>
                    <td className="px-5 py-4"><ContentStatusBadge value={row.status} /></td>
                    <td className="px-5 py-4 text-sm text-ink/50">{formatDate(row.createdAt)}</td>
                    {canMutate && (
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => setSelected(row)}
                          disabled={removing || bulkRemoving}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} disabled={loadedRows === null} />

      <Modal
        isOpen={selected !== null || bulkRemoveOpen}
        onClose={() => {
          setSelected(null)
          setBulkRemoveOpen(false)
        }}
        title={bulkRemoveOpen ? "Remove selected subscribers" : "Remove subscriber"}
        maxWidth="sm"
      >
        {bulkRemoveOpen ? (
          <p className="text-sm leading-6 text-ink/70">
            Remove <strong className="font-semibold text-ink">{selectedCount} subscribers</strong> from the newsletter list? They will stop receiving future updates.
          </p>
        ) : (
          <p className="text-sm leading-6 text-ink/70">
            Remove <strong className="font-semibold text-ink">{selected?.email}</strong> from the newsletter list? They will stop receiving future updates.
          </p>
        )}
        <ModalActions>
          <ModalButton onClick={() => { setSelected(null); setBulkRemoveOpen(false) }} disabled={removing || bulkRemoving}>Cancel</ModalButton>
          <ModalButton onClick={() => void (bulkRemoveOpen ? removeSelected() : remove())} variant="danger" disabled={removing || bulkRemoving}>
            {removing || bulkRemoving ? "Removing..." : bulkRemoveOpen ? "Remove selected" : "Remove subscriber"}
          </ModalButton>
        </ModalActions>
      </Modal>
    </div>
  )
}
